/**
 * Slugs de los enlaces cortos.
 *
 * El slug es lo unico que viaja dentro del QR, asi que cada caracter de mas
 * densifica el codigo y lo hace mas dificil de escanear de lejos o impreso en
 * pequeno. De ahi la longitud corta y el alfabeto reducido.
 */

/**
 * Alfabeto sin caracteres que se confunden al leerlos o al teclearlos a mano:
 * fuera 0/o, 1/l/i. Quien no pueda escanear el codigo tendra que copiar la
 * direccion mirandola, y ahi un 0 y una O cuestan una visita perdida.
 */
export const SLUG_ALPHABET = '23456789abcdefghjkmnpqrstuvwxyz';

export const SLUG_LENGTH = 7;
export const SLUG_MIN_LENGTH = 3;
export const SLUG_MAX_LENGTH = 48;

/**
 * Prefijos que el acortador necesita para si mismo. Un cliente no puede
 * reservarlos como slug personalizado.
 */
export const RESERVED_SLUGS: ReadonlySet<string> = new Set([
  'api',
  'admin',
  'app',
  'auth',
  'login',
  'logout',
  'signup',
  'dashboard',
  'settings',
  'billing',
  'pricing',
  'help',
  'support',
  'docs',
  'blog',
  'legal',
  'privacy',
  'terms',
  'about',
  'contact',
  'status',
  'health',
  'static',
  'assets',
  'public',
  'favicon',
  'robots',
  'sitemap',
  'manifest',
  'sw',
  'expired',
  'blocked',
  'not-found',
  'preview',
  'qr',
  'r',
  's',
  'go',
  'www',
  'mail',
  'ftp',
  'cdn',
  'test',
  'debug',
  'internal',
  'firebase',
  '__',
]);

const SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

export type SlugRejectionReason =
  | 'too_short'
  | 'too_long'
  | 'invalid_chars'
  | 'reserved'
  | 'looks_like_file';

export interface SlugCheckResult {
  readonly ok: boolean;
  readonly reason?: SlugRejectionReason;
  readonly normalized?: string;
}

/**
 * Los slugs se guardan y se buscan siempre en minusculas: el usuario que
 * teclea la direccion a mano no distingue mayusculas, y dos codigos que solo
 * se diferencien por la caja serian una trampa de phishing.
 */
export function normalizeSlug(input: string): string {
  return input.trim().toLowerCase();
}

export function checkSlug(input: string): SlugCheckResult {
  const slug = normalizeSlug(input);

  // Los reservados se miran primero: varios (r, go, qr) son mas cortos que el
  // minimo, y si se comprobara la longitud antes nunca se llegaria a la lista.
  if (RESERVED_SLUGS.has(slug)) return { ok: false, reason: 'reserved' };
  if (slug.length < SLUG_MIN_LENGTH) return { ok: false, reason: 'too_short' };
  if (slug.length > SLUG_MAX_LENGTH) return { ok: false, reason: 'too_long' };
  // Se comprueba antes que el patron general: el punto tambien lo rechazaria
  // ese patron, pero con un mensaje que no le dice nada al usuario.
  if (/\.[a-z0-9]{2,4}$/.test(slug)) return { ok: false, reason: 'looks_like_file' };
  if (!SLUG_PATTERN.test(slug)) return { ok: false, reason: 'invalid_chars' };

  return { ok: true, normalized: slug };
}

/**
 * Genera un slug aleatorio con `crypto.getRandomValues`, disponible tanto en
 * el navegador como en Node. Se descarta el sesgo del modulo para que todos
 * los slugs sean igual de probables: con un alfabeto de 31 caracteres el
 * sesgo seria pequeno, pero un espacio de claves uniforme es lo que hace que
 * no se puedan adivinar codigos ajenos a base de probar.
 */
export function generateSlug(length: number = SLUG_LENGTH): string {
  const alphabet = SLUG_ALPHABET;
  const max = Math.floor(256 / alphabet.length) * alphabet.length;
  let out = '';

  while (out.length < length) {
    const bytes = new Uint8Array(length - out.length + 8);
    crypto.getRandomValues(bytes);
    for (const byte of bytes) {
      if (out.length === length) break;
      if (byte >= max) continue; // descarta para no sesgar
      out += alphabet[byte % alphabet.length];
    }
  }

  return out;
}

/** Enlace corto completo tal y como se codifica en el QR. */
export function buildShortLink(base: string, slug: string): string {
  return `${base.replace(/\/+$/, '')}/${slug}`;
}

/**
 * Base del enlace corto a partir de un nombre de host.
 *
 * Solo el desarrollo va sin cifrar: en cualquier otro sitio, un QR que lleve a
 * http es una invitacion a que alguien por el medio cambie el destino, y el
 * cartel ya esta impreso.
 */
export function shortLinkBase(host: string): string {
  if (!host) return '';
  const esLocal = /^(localhost|127\.|\[?::1)/i.test(host);
  return `${esLocal ? 'http' : 'https'}://${host.replace(/\/+$/, '')}`;
}

/**
 * Enlace corto de un codigo, respetando el dominio con el que nacio.
 *
 * Un QR impreso lleva grabado el dominio del dia en que se genero. Si el
 * servicio se muda a otro, el panel NO debe empezar a mostrar el nuevo para un
 * codigo viejo: ensenaria una direccion que no coincide con la del cartel, y
 * volver a descargarlo daria una imagen distinta de la que esta pegada en la
 * pared. Cada codigo conserva su verdad.
 *
 * Los creados antes de guardarse el host caen en la base actual, que es lo que
 * se venia haciendo siempre.
 */
export function shortLinkOf(
  code: { readonly slug: string; readonly shortHost?: string | null },
  baseActual: string,
): string {
  const base = code.shortHost ? shortLinkBase(code.shortHost) : baseActual;
  return buildShortLink(base, code.slug);
}

export const SLUG_REJECTION_MESSAGES: Readonly<Record<SlugRejectionReason, string>> = {
  too_short: `El código debe tener al menos ${SLUG_MIN_LENGTH} caracteres.`,
  too_long: `El código no puede superar los ${SLUG_MAX_LENGTH} caracteres.`,
  invalid_chars: 'Usa solo letras minúsculas, números y guiones.',
  reserved: 'Ese código está reservado por el sistema. Elige otro.',
  looks_like_file: 'El código no puede terminar como un nombre de archivo.',
};
